/**
 * MapLibre GL configuration and default settings
 */

import type { StyleSpecification } from 'maplibre-gl';

import { isFeatureInScope, type Scope } from './mapFeatures';
import { POLAND_COUNTRY_CODE } from '../constants/country';
import type { MapSettings } from '../types/workspace/MapSettings';

const OPENMAPTILES_GLYPHS =
  'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';

interface RasterBasemapOptions {
  id: string;
  tiles: string[];
  attribution: string;
  sourceMaxzoom: number;
  bounds?: [number, number, number, number];
}

function createRasterBasemapStyle({
  id,
  tiles,
  attribution,
  sourceMaxzoom,
  bounds,
}: RasterBasemapOptions) {
  return {
    version: 8 as const,
    sources: {
      [id]: {
        type: 'raster' as const,
        tiles,
        tileSize: 256,
        attribution,
        maxzoom: sourceMaxzoom,
        ...(bounds ? { bounds } : {}),
      },
    },
    layers: [
      {
        id,
        type: 'raster' as const,
        source: id,
        minzoom: 0,
        maxzoom: 22,
      },
    ],
    glyphs: OPENMAPTILES_GLYPHS,
  };
}

// OSM raster tiles (free, ODbL)
const DEFAULT_MAP_STYLE = createRasterBasemapStyle({
  id: 'osm-tiles',
  tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
  attribution:
    '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  sourceMaxzoom: 19,
});

// CARTO Dark Matter (free, no API key, CC-BY 4.0)
const CARTO_DARK_STYLE =
  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

// OpenFreeMap vector tiles (used with terrain elevation)
export const VECTOR_3D_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

// Esri World Imagery (free, no API key, attribution required)
const ESRI_SATELLITE_STYLE = createRasterBasemapStyle({
  id: 'esri-satellite',
  tiles: [
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  ],
  attribution:
    'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  sourceMaxzoom: 19,
});

// OpenTopoMap (free, CC-BY-SA, fair-use)
const OPENTOPO_STYLE = createRasterBasemapStyle({
  id: 'opentopo-tiles',
  tiles: [
    'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
    'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
    'https://c.tile.opentopomap.org/{z}/{x}/{y}.png',
  ],
  attribution:
    'Map data: © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: © <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
  sourceMaxzoom: 17,
});

// CyclOSM (free, ODbL)
const CYCLOSM_STYLE = createRasterBasemapStyle({
  id: 'cyclosm-tiles',
  tiles: [
    'https://a.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
    'https://b.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
    'https://c.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
  ],
  attribution:
    '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Style: <a href="https://www.cyclosm.org">CyclOSM</a>',
  sourceMaxzoom: 20,
});

// Geoportal PL ortho (GUGiK public data, bounds-limited to Poland)
const GEOPORTAL_ORTHO_STYLE = createRasterBasemapStyle({
  id: 'geoportal-ortho',
  tiles: [
    'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMTS/StandardResolution?service=WMTS&request=GetTile&version=1.0.0&layer=ORTOFOTOMAPA&tilematrixset=EPSG:3857&TileMatrix=EPSG:3857:{z}&TileCol={x}&TileRow={y}&format=image/jpeg',
  ],
  attribution:
    'Ortofotomapa © <a href="https://www.geoportal.gov.pl">GUGiK / Geoportal.gov.pl</a>',
  sourceMaxzoom: 19,
  bounds: [14.0, 49.0, 24.2, 55.0],
});

// ===== BASEMAP REGISTRY =====

export type BasemapId =
  | 'auto'
  | 'osm'
  | 'carto-dark'
  | 'satellite'
  | 'opentopo'
  | 'cyclosm'
  | 'geoportal-ortho';

export interface BasemapDefinition {
  id: BasemapId;
  labelKey: string;
  style: StyleSpecification | string | null;
  thumbnail: string;
  /**
   * Country scope for this basemap: an ISO 3166-1 alpha-2 code the basemap is
   * restricted to (e.g. 'PL' for geoportal-ortho, bounds-limited to Poland), or
   * `undefined` for a globally-available basemap. Same scope model as the
   * standard layers (see {@link StandardLayerDefinition.scope} in
   * `mapFeatures.ts`). Mirror of backend
   * WorkspaceConstants.BASEMAP_COUNTRY_RESTRICTIONS.
   */
  scope?: Scope;
}

/** 'auto' has style=null — resolved at runtime based on color scheme */
export const BASEMAPS: BasemapDefinition[] = [
  {
    id: 'auto',
    labelKey: 'layers.basemap.auto',
    style: null,
    thumbnail: '/data/basemap-auto.png',
  },
  {
    id: 'osm',
    labelKey: 'layers.basemap.osm',
    style: DEFAULT_MAP_STYLE,
    thumbnail: '/data/basemap-osm.png',
  },
  {
    id: 'carto-dark',
    labelKey: 'layers.basemap.cartoDark',
    style: CARTO_DARK_STYLE,
    thumbnail: '/data/basemap-carto-dark.png',
  },
  {
    id: 'satellite',
    labelKey: 'layers.basemap.satellite',
    style: ESRI_SATELLITE_STYLE,
    thumbnail: '/data/basemap-satellite.png',
  },
  {
    id: 'opentopo',
    labelKey: 'layers.basemap.opentopo',
    style: OPENTOPO_STYLE,
    thumbnail: '/data/basemap-opentopo.png',
  },
  {
    id: 'cyclosm',
    labelKey: 'layers.basemap.cyclosm',
    style: CYCLOSM_STYLE,
    thumbnail: '/data/basemap-cyclosm.png',
  },
  {
    id: 'geoportal-ortho',
    labelKey: 'layers.basemap.geoportalOrtho',
    style: GEOPORTAL_ORTHO_STYLE,
    thumbnail: '/data/basemap-geoportal.png',
    scope: POLAND_COUNTRY_CODE,
  },
];

export const DEFAULT_BASEMAP_ID: BasemapId = 'auto';

/** Look up a basemap definition by id in the canonical {@link BASEMAPS} registry. */
export function getBasemapById(
  basemapId: string
): BasemapDefinition | undefined {
  return BASEMAPS.find((basemap) => basemap.id === basemapId);
}

/**
 * Resolves a basemap id to a concrete MapLibre style. Pure (no store / Mantine)
 * so it is reusable outside React, e.g. the map-config live preview.
 * `'auto'` resolves to the dark/light style based on the color scheme.
 */
export function resolveBasemapStyle(
  basemapId: BasemapId,
  colorScheme: 'light' | 'dark'
): StyleSpecification | string {
  if (basemapId === 'auto') {
    return colorScheme === 'dark' ? CARTO_DARK_STYLE : DEFAULT_MAP_STYLE;
  }
  return getBasemapById(basemapId)?.style ?? DEFAULT_MAP_STYLE;
}

/** Type guard: whether a string is a known basemap id. */
export function isValidBasemapId(basemapId: string): basemapId is BasemapId {
  return getBasemapById(basemapId) !== undefined;
}

/**
 * Effective default basemap for a workspace: its configured default, or
 * `'auto'` for workspaces predating the basemap configurator (empty / legacy
 * map settings, or an unknown id).
 */
export function getDefaultBasemapId(
  mapSettings: MapSettings | null
): BasemapId {
  const configured = mapSettings?.defaultBasemapId;
  return configured && isValidBasemapId(configured)
    ? configured
    : DEFAULT_BASEMAP_ID;
}

/**
 * Returns `candidate` when it is a known basemap id still present in
 * `allowedBasemaps`, otherwise `undefined`. Single source of the "clamp a
 * candidate basemap to an allow-list" rule shared by the share form, the public
 * hydration, and the runtime basemap selector.
 */
export function clampBasemapToAllowed(
  candidate: string | null | undefined,
  allowedBasemaps: BasemapId[]
): BasemapId | undefined {
  return candidate != null &&
    isValidBasemapId(candidate) &&
    allowedBasemaps.includes(candidate)
    ? candidate
    : undefined;
}

/**
 * Resolves the default (start) basemap to a value inside `allowedBasemaps`:
 * keeps the candidate when it is still allowed, otherwise snaps to the first
 * allowed basemap (or `'auto'` when none are allowed). Single source of the
 * "keep the default within the allowlist" rule shared by the map configurator,
 * the share form, and the share-form mapping.
 */
export function resolveDefaultBasemap(
  candidate: string | null | undefined,
  allowedBasemaps: BasemapId[]
): BasemapId {
  return (
    clampBasemapToAllowed(candidate, allowedBasemaps) ??
    allowedBasemaps[0] ??
    DEFAULT_BASEMAP_ID
  );
}

/**
 * Basemaps available for a workspace's country: every basemap whose
 * {@link BasemapDefinition.scope} is unset or matches the country code (e.g. the
 * Geoportal orthophoto is offered only for Poland). No workspace allowlist is
 * applied — the country-valid set before {@link getVisibleBasemaps} narrows it.
 */
function getCountryAllowedBasemaps(
  workspaceCountryCode: string | null
): BasemapDefinition[] {
  return BASEMAPS.filter((basemap) =>
    isFeatureInScope(basemap.scope, workspaceCountryCode)
  );
}

/**
 * Basemaps offered in the layers menu for a workspace: the configured
 * allowlist (order preserved from {@link BASEMAPS}), or all basemaps for legacy
 * workspaces. Country-restricted basemaps (e.g. the Geoportal orthophoto, whose
 * tiles are bounds-limited to Poland) are filtered out unless the workspace's
 * country code matches their {@link BasemapDefinition.scope}.
 */
export function getVisibleBasemaps(
  mapSettings: MapSettings | null,
  workspaceCountryCode: string | null
): BasemapDefinition[] {
  const allowed = mapSettings?.allowedBasemapIds;
  const countryAllowed = getCountryAllowedBasemaps(workspaceCountryCode);
  return allowed && allowed.length > 0
    ? countryAllowed.filter((basemap) => allowed.includes(basemap.id))
    : countryAllowed;
}

// Terrain DEM source configuration (backend proxy)
export const TERRAIN_SOURCE_ID = 'terrain-dem';

export const TERRAIN_DEM_SOURCE = {
  type: 'raster-dem' as const,
  tiles: ['/api/v1/tiles/terrain/{z}/{x}/{y}.webp'],
  tileSize: 256,
  maxzoom: 12,
};

// Terrain 3D display constants
export const TERRAIN_3D_PITCH = 55;
export const TERRAIN_3D_EXAGGERATION = 1.5;
export const TERRAIN_TRANSITION_MS = 800;

// Default map center coordinates (Poland)
export const DEFAULT_CENTER: [number, number] = [19.1343786, 51.9194675];

// Default zoom level
export const DEFAULT_ZOOM = 6.0;

// Default pitch and bearing
const DEFAULT_PITCH = 0;
const DEFAULT_BEARING = 0;

// Map configuration object
export const MAP_CONFIG = {
  center: DEFAULT_CENTER,
  zoom: DEFAULT_ZOOM,
  pitch: DEFAULT_PITCH,
  bearing: DEFAULT_BEARING,
  style: DEFAULT_MAP_STYLE,
  maxZoom: 18,
  minZoom: 4,
  doubleClickZoom: true,
  dragPan: true,
  dragRotate: true,
  scrollZoom: true,
  touchZoom: true,
  touchRotate: true,
  keyboard: true,
  hash: false, // Whether to sync map state to URL hash
  attributionControl: true,
  logoControl: true,
} as const;

// Event throttling configuration
export const EVENT_CONFIG = {
  // Map event throttling delays (in milliseconds)
  moveThrottle: 100, // Throttle move events
  clickDebounce: 50, // Debounce click events
  resizeDebounce: 250, // Debounce resize events

  // Performance thresholds
  maxFrameTime: 16.67, // Target 60fps (16.67ms per frame)
  performanceDegradationThreshold: 33, // Performance warning at 30fps
} as const;
